// Search input + 6 filter chips above the Insurance/Licence list. Mirrors
// the deliverables FilterChipBar pattern: 200ms-debounced search, three
// multi-select chips (Type, Status, Provider), two binary warning chips
// (Evidence missing, Below minimum), and a trailing Include-archived chip.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  INSURANCE_CATEGORY_LABELS,
  type Insurance,
  type InsuranceCategory,
  type InsuranceStatus,
} from "../../../../types";
import DebouncedSearchInput from "../shared/DebouncedSearchInput";

export interface InsuranceFilterChipBarFilters {
  category: InsuranceCategory[];
  status: InsuranceStatus[];
  provider: string[];
  evidenceMissing: boolean;
  belowMinimum: boolean;
  includeArchived: boolean;
}

export interface InsuranceFilterChipBarProps {
  filters: InsuranceFilterChipBarFilters;
  onFiltersChange: (next: InsuranceFilterChipBarFilters) => void;
  search: string;
  onSearchChange: (next: string) => void;
  /** Source list used to auto-populate the Provider chip popover. */
  insurances: Insurance[];
  /** Bump to force the local search-input state to resync with the
   *  `search` prop AND drop any in-flight debounce. The parent
   *  increments this on Clear-all so the reset works even when the
   *  typed text hasn't yet flushed through the debounce (in which case
   *  `search` is unchanged and a `[search]` useEffect won't fire). */
  resetCounter?: number;
}

interface OptionDef<V extends string> {
  value: V;
  label: string;
}

const TYPE_OPTIONS: OptionDef<InsuranceCategory>[] = (
  Object.keys(INSURANCE_CATEGORY_LABELS) as InsuranceCategory[]
).map((value) => ({ value, label: INSURANCE_CATEGORY_LABELS[value] }));

const STATUS_OPTIONS: OptionDef<InsuranceStatus>[] = [
  { value: "upcoming", label: "Future" },
  { value: "current", label: "Current" },
  { value: "expiring", label: "Expiring soon" },
  { value: "expired", label: "Expired" },
];

const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-caption font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400";

const CHIP_INACTIVE =
  "border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700";

const CHIP_ACTIVE =
  "border-brand-400 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950 dark:text-brand-300";

const POPOVER_CLASS =
  "absolute z-20 mt-1 min-w-[180px] max-h-[60vh] overflow-y-auto rounded-md border border-slate-200 bg-white p-2 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800";

interface MultiSelectChipProps<V extends string> {
  chipKey: "type" | "status" | "provider";
  label: string;
  options: OptionDef<V>[];
  selected: V[];
  onToggle: (next: V[]) => void;
}

function MultiSelectChip<V extends string>({
  chipKey,
  label,
  options,
  selected,
  onToggle,
}: MultiSelectChipProps<V>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isActive = selected.length > 0;

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open]);

  const handleToggleOption = (value: V) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onToggle(next);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${CHIP_BASE} ${isActive ? CHIP_ACTIVE : CHIP_INACTIVE}`}
      >
        <span>{label}</span>
        {isActive && (
          <span className="rounded bg-white px-1 text-[10px] text-brand-600 dark:bg-brand-900 dark:text-brand-300">
            {selected.length}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid={`filter-popover-${chipKey}`}
          role="listbox"
          aria-multiselectable="true"
          className={POPOVER_CLASS}
        >
          <ul className="space-y-1">
            {options.map((opt) => {
              const id = `filter-${chipKey}-${opt.value}`;
              return (
                <li key={opt.value}>
                  <label
                    htmlFor={id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={selected.includes(opt.value)}
                      onChange={() => handleToggleOption(opt.value)}
                      className="accent-brand-600"
                    />
                    <span>{opt.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

interface BinaryChipProps {
  label: string;
  active: boolean;
  onToggle: () => void;
}

function BinaryChip({ label, active, onToggle }: BinaryChipProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className={`${CHIP_BASE} ${active ? CHIP_ACTIVE : CHIP_INACTIVE}`}
    >
      {label}
    </button>
  );
}

// This bar's search box uses `rounded-md` (not the `rounded-full` every
// other tracking chip bar uses) — the corner radius is deliberate here,
// not drift; preserve it when touching this file.
const INSURANCE_SEARCH_INPUT_CLASS =
  "h-8 w-72 rounded-md border border-slate-300 bg-white pl-7 pr-3 text-caption text-slate-700 focus:border-brand-400 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200";

export default function InsuranceFilterChipBar({
  filters,
  onFiltersChange,
  search,
  onSearchChange,
  insurances,
  resetCounter,
}: InsuranceFilterChipBarProps) {
  const update = <K extends keyof InsuranceFilterChipBarFilters>(
    key: K,
    value: InsuranceFilterChipBarFilters[K],
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const providerOptions = useMemo<OptionDef<string>[]>(() => {
    const set = new Set<string>();
    for (const i of insurances) {
      if (i.provider) set.add(i.provider);
    }
    return Array.from(set)
      .sort()
      .map((value) => ({ value, label: value }));
  }, [insurances]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <DebouncedSearchInput
        value={search}
        onDebouncedChange={onSearchChange}
        resetCounter={resetCounter}
        ariaLabel="Search insurances"
        placeholder="Search insurances, providers, policy numbers, clauses…"
        inputClassName={INSURANCE_SEARCH_INPUT_CLASS}
      />

      <MultiSelectChip<InsuranceCategory>
        chipKey="type"
        label="Type"
        options={TYPE_OPTIONS}
        selected={filters.category}
        onToggle={(next) => update("category", next)}
      />
      <MultiSelectChip<InsuranceStatus>
        chipKey="status"
        label="Status"
        options={STATUS_OPTIONS}
        selected={filters.status}
        onToggle={(next) => update("status", next)}
      />
      <MultiSelectChip<string>
        chipKey="provider"
        label="Provider"
        options={providerOptions}
        selected={filters.provider}
        onToggle={(next) => update("provider", next)}
      />
      <BinaryChip
        label="Evidence missing"
        active={filters.evidenceMissing}
        onToggle={() => update("evidenceMissing", !filters.evidenceMissing)}
      />
      <BinaryChip
        label="Below minimum"
        active={filters.belowMinimum}
        onToggle={() => update("belowMinimum", !filters.belowMinimum)}
      />
      <BinaryChip
        label="Include archived"
        active={filters.includeArchived}
        onToggle={() => update("includeArchived", !filters.includeArchived)}
      />
    </div>
  );
}
